// feature/informes-mensuales-v1 — sprint84.
//
// Lean MVP for the monthly Previsora contract 225-2025 report.
// Admin/directora pick a month, see live platform metrics, optionally
// patch the off-platform numbers (memoriales 1a/2a, sanciones cierres)
// + per-section notes, and download the filled .docx.
//
// Persistence: GET/PUT /api/informes/:period_start stores the manual
// metrics so the same admin (or another) can regenerate without
// retyping. Generation: POST /api/informes/:period_start/generate
// streams back a .docx (Content-Disposition: attachment).

import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../../api/client";
import { informes as informesApi } from "../../api/endpoints";
import Spinner from "../../components/Spinner";
import { useToast } from "../../components/Toast";

const MONTH_LABEL = (yyyyMm) => {
  const [y, m] = yyyyMm.split("-").map(Number);
  const names = ["enero","febrero","marzo","abril","mayo","junio",
                 "julio","agosto","septiembre","octubre","noviembre","diciembre"];
  return `${names[m - 1]} ${y}`;
};

// Half-open period from a YYYY-MM string. Returns [start, end) as YYYY-MM-DD.
function periodOf(yyyyMm) {
  const [y, m] = yyyyMm.split("-").map(Number);
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1     : m + 1;
  const start = `${y}-${String(m).padStart(2, "0")}-01`;
  const end   = `${ny}-${String(nm).padStart(2, "0")}-01`;
  return { start, end };
}

function defaultMonthYyyyMm() {
  const now = new Date();
  // Default: current month if we're in the first 8 days OR last 7 days,
  // otherwise previous month. Sprint84 ships on May 28 for the May
  // informe (due May 31), so the "last 7 days" branch fires here.
  const dom = now.getDate();
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const useCurrent = dom <= 8 || dom >= last - 6;
  const target = useCurrent ? now : new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, "0")}`;
}

function MetricRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", fontSize: 13 }}>
      <span>{label}</span>
      <strong>{value == null ? "—" : value}</strong>
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="card tight" style={{ padding: 14 }}>
      <h3 style={{ margin: "0 0 8px 0", fontSize: 14 }}>{title}</h3>
      {children}
    </div>
  );
}

export default function InformeMensualPanel() {
  const toast = useToast();
  const location = useLocation();
  const [month, setMonth] = useState(defaultMonthYyyyMm());
  const [metrics, setMetrics]   = useState(null);
  const [stored,  setStored]    = useState(null);
  const [manual,  setManual]    = useState({});
  const [notesPerSection, setNotesPerSection] = useState({});
  const [busy,    setBusy]      = useState(false);
  const [busySave,setBusySave]  = useState(false);
  const [busyGen, setBusyGen]   = useState(false);

  const { start, end } = useMemo(() => periodOf(month), [month]);

  // sprint84.1 — sidebar entry "Informe Previsora" links to
  // /admin/exports#informe-mensual. React Router 6 doesn't auto-scroll
  // to hash anchors, so handle it here once the panel mounts.
  useEffect(() => {
    if (location.hash !== "#informe-mensual") return;
    const t = setTimeout(() => {
      const el = document.getElementById("informe-mensual");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
    return () => clearTimeout(t);
  }, [location.hash]);

  // Load metrics + stored draft on month change.
  useEffect(() => {
    let alive = true;
    setBusy(true); setMetrics(null); setStored(null);
    Promise.all([
      informesApi.metrics(start, end),
      informesApi.get(start),
    ]).then(([mRes, sRes]) => {
      if (!alive) return;
      setMetrics(mRes.data.metrics);
      const s = sRes.data;
      setStored(s);
      setManual((s && s.manual_metrics) || {});
      setNotesPerSection((s && s.manual_metrics && s.manual_metrics.notes_per_section) || {});
    }).catch(() => {
      if (alive) toast.push("Error al cargar el informe", "error");
    }).finally(() => alive && setBusy(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  function setManualField(section, field, raw) {
    const val = raw === "" ? "" : Number(raw);
    setManual((m) => ({ ...m, [section]: { ...(m[section] || {}), [field]: val } }));
  }
  function setNoteFor(section, raw) {
    setNotesPerSection((n) => ({ ...n, [section]: raw }));
  }

  async function save() {
    setBusySave(true);
    try {
      const body = {
        manual_metrics: { ...manual, notes_per_section: notesPerSection },
        notes: null,
      };
      const r = await informesApi.put(start, body);
      setStored(r.data);
      toast.push("Borrador guardado");
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al guardar", "error");
    } finally { setBusySave(false); }
  }

  async function generate() {
    setBusyGen(true);
    try {
      // Save current edits first so the .docx reflects what the user sees.
      await save();
      // Stream the .docx file back via the configured api client.
      const r = await api.post(`/api/informes/${start}/generate`, {}, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Informe_EDYS_${start.slice(0, 7)}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.push("Informe generado");
    } catch (e) {
      toast.push(e?.response?.data?.error || "Error al generar el informe", "error");
    } finally { setBusyGen(false); }
  }

  const m = metrics || {};

  return (
    <div id="informe-mensual" className="col" style={{ gap: 16, scrollMarginTop: 16 }}>
      <h1 style={{ margin: 0 }}>Informe Mensual — Contrato 225-2025</h1>
      <div className="muted" style={{ marginTop: -8 }}>
        Métricas automáticas de la plataforma + campos manuales para datos externos.
        Descargue el .docx para enviar a Previsora.
      </div>

      <div className="card">
        <div className="row" style={{ gap: 12, alignItems: "end", flexWrap: "wrap" }}>
          <div>
            <label>Mes</label>
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              Período: <strong>{MONTH_LABEL(month)}</strong>{" "}
              {stored && stored.updated_at && (
                <span>· última edición {new Date(stored.updated_at).toLocaleString("es-CO")}{stored.updated_by_name ? ` por ${stored.updated_by_name}` : ""}</span>
              )}
              {!stored && !busy && <span>· sin borrador</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="secondary" onClick={save} disabled={busy || busySave || busyGen}>
              {busySave && <Spinner color="currentColor" />} Guardar borrador
            </button>
            <button onClick={generate} disabled={busy || busyGen}>
              {busyGen && <Spinner color="currentColor" />} Generar informe (.docx)
            </button>
          </div>
        </div>
      </div>

      {busy && (
        <div className="card" style={{ padding: 32, textAlign: "center" }}><Spinner size={20} /></div>
      )}

      {!busy && metrics && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
          {/* 1. Tutelas */}
          <SectionCard title="1. Tutelas">
            <MetricRow label="Recibidas"          value={m.tutelas?.recibidas} />
            <MetricRow label="Tramitadas"         value={m.tutelas?.tramitadas} />
            <MetricRow label="Insumos solicitados" value={m.tutelas?.insumos_solicitados} />
            <MetricRow label="Insumos recibidos"   value={m.tutelas?.insumos_recibidos} />
            <NoteEditor section="tutelas" value={notesPerSection.tutelas || ""} onChange={(v) => setNoteFor("tutelas", v)} />
          </SectionCard>

          {/* 2. Fallos 1a — sprint84.2 adds the Nulidades row (separate
              in the UI for internal audit; merged into "Favorables" in
              the .docx per Previsora contract template). */}
          <SectionCard title="2. Fallos 1ª Instancia">
            <MetricRow label="Favorables"         value={m.fallos_1a?.favorables} />
            <MetricRow label="Desfavorables"      value={m.fallos_1a?.desfavorables} />
            <MetricRow label="Nulidades"          value={m.fallos_1a?.nulidades} />
            <MetricRow label="Impugnaciones"      value={m.fallos_1a?.impugnaciones} />
            <ManualField label="Memoriales de cumplimiento (manual)"
              value={manual.fallos_1a?.memoriales_cumplimiento ?? ""}
              onChange={(v) => setManualField("fallos_1a", "memoriales_cumplimiento", v)} />
            <MetricRow label="Insumos solicitados" value={m.fallos_1a?.insumos_solicitados} />
            <MetricRow label="Insumos recibidos"   value={m.fallos_1a?.insumos_recibidos} />
            <div className="muted" style={{ fontSize: 11, marginTop: 8, padding: "6px 8px", background: "var(--accent-mint-bg)", borderLeft: "2px solid var(--accent-mint)", borderRadius: 3 }}>
              ⓘ En el informe .docx, las nulidades se incluyen dentro de “Fallos favorables” según el formato del contrato con Previsora. El conteo desagregado se muestra aquí para auditoría interna.
            </div>
            <NoteEditor section="fallos_1a" value={notesPerSection.fallos_1a || ""} onChange={(v) => setNoteFor("fallos_1a", v)} />
          </SectionCard>

          {/* 3. Fallos 2a */}
          <SectionCard title="3. Fallos 2ª Instancia">
            <MetricRow label="Favorables"         value={m.fallos_2a?.favorables} />
            <MetricRow label="Desfavorables"      value={m.fallos_2a?.desfavorables} />
            <MetricRow label="Impugnaciones"      value={m.fallos_2a?.impugnaciones} />
            <ManualField label="Memoriales de cumplimiento (manual)"
              value={manual.fallos_2a?.memoriales_cumplimiento ?? ""}
              onChange={(v) => setManualField("fallos_2a", "memoriales_cumplimiento", v)} />
            <MetricRow label="Insumos solicitados" value={m.fallos_2a?.insumos_solicitados} />
            <MetricRow label="Insumos recibidos"   value={m.fallos_2a?.insumos_recibidos} />
            <NoteEditor section="fallos_2a" value={notesPerSection.fallos_2a || ""} onChange={(v) => setNoteFor("fallos_2a", v)} />
          </SectionCard>

          {/* 4. Requerimientos */}
          <SectionCard title="4. Requerimientos">
            <MetricRow label="Recibidos"          value={m.requerimientos?.recibidos} />
            <MetricRow label="Tramitados"         value={m.requerimientos?.tramitados} />
            <MetricRow label="Insumos solicitados" value={m.requerimientos?.insumos_solicitados} />
            <MetricRow label="Insumos recibidos"   value={m.requerimientos?.insumos_recibidos} />
            <NoteEditor section="requerimientos" value={notesPerSection.requerimientos || ""} onChange={(v) => setNoteFor("requerimientos", v)} />
          </SectionCard>

          {/* 5. Desacatos */}
          <SectionCard title="5. Desacatos">
            <MetricRow label="Aperturas"          value={m.desacatos?.aperturas} />
            <MetricRow label="Cierres"            value={m.desacatos?.cierres} />
            <MetricRow label="Tramitados"         value={m.desacatos?.tramitados} />
            <MetricRow label="Insumos solicitados" value={m.desacatos?.insumos_solicitados} />
            <MetricRow label="Insumos recibidos"   value={m.desacatos?.insumos_recibidos} />
            <NoteEditor section="desacatos" value={notesPerSection.desacatos || ""} onChange={(v) => setNoteFor("desacatos", v)} />
          </SectionCard>

          {/* 6. Sanciones */}
          <SectionCard title="6. Sanciones">
            <MetricRow label="Aperturas"          value={m.sanciones?.aperturas} />
            <MetricRow label="Tramitadas"         value={m.sanciones?.tramitadas} />
            <ManualField label="Cierres (manual)"
              value={manual.sanciones?.cierres ?? ""}
              onChange={(v) => setManualField("sanciones", "cierres", v)} />
            <MetricRow label="Pendientes (snapshot)" value={m.sanciones?.pendientes} />
            <NoteEditor section="sanciones" value={notesPerSection.sanciones || ""} onChange={(v) => setNoteFor("sanciones", v)} />
          </SectionCard>

          {/* 7. Consolidado */}
          <SectionCard title="7. Insumos consolidado">
            <MetricRow label="Total solicitados"  value={m.insumos_consolidado?.total_solicitados} />
            <MetricRow label="Total recibidos"    value={m.insumos_consolidado?.total_recibidos} />
            <MetricRow label="Pendientes"         value={m.insumos_consolidado?.pendientes} />
            <MetricRow label="% cumplimiento"
              value={m.insumos_consolidado?.porcentaje_cumplimiento == null
                ? "—"
                : `${m.insumos_consolidado.porcentaje_cumplimiento}%`} />
            <NoteEditor section="insumos_consolidado" value={notesPerSection.insumos_consolidado || ""} onChange={(v) => setNoteFor("insumos_consolidado", v)} />
          </SectionCard>
        </div>
      )}
    </div>
  );
}

function ManualField({ label, value, onChange }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "4px 0", fontSize: 13, gap: 8 }}>
      <span style={{ color: "var(--color-warning-ink)" }}>{label}</span>
      <input type="number" min={0} step={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 80, textAlign: "right" }} />
    </div>
  );
}

function NoteEditor({ section, value, onChange }) {
  const [open, setOpen] = useState(!!value);
  return (
    <div style={{ marginTop: 8 }}>
      {!open && !value && (
        <button className="ghost sm" onClick={() => setOpen(true)} style={{ fontSize: 11 }}>
          + Agregar observación
        </button>
      )}
      {(open || value) && (
        <>
          <label style={{ fontSize: 11, color: "var(--text-muted)" }}>Observaciones</label>
          <textarea rows={2} value={value}
            placeholder="Notas internas — se imprimen bajo la tabla."
            onChange={(e) => onChange(e.target.value)}
            style={{ width: "100%", fontSize: 12 }} />
        </>
      )}
    </div>
  );
}
